import {
	Avatar,
	Badge,
	Box,
	Button,
	Divider,
	Flex,
	Grid,
	HStack,
	Icon,
	IconButton,
	SimpleGrid,
	Text,
	Tooltip,
	useColorModeValue
} from '@chakra-ui/react';
import { FaWhatsapp } from 'react-icons/fa';
import { MdContentCopy, MdLocationOn, MdPhone, MdTrendingUp, MdCreditScore, MdEmail } from 'react-icons/md';
import Card from '../../components/card/Card';
import { ConsultaResult, isFlag } from '../../models/consulta.model';
import { toastService } from '../../services/toast.service';
import { formatPrice } from '../../utils/format';

interface ResultCardProps {
	result: ConsultaResult;
}

function initialsOf(name?: string) {
	if (!name) return '?';
	return name
		.split(' ')
		.map(n => n[0])
		.slice(0, 2)
		.join('')
		.toUpperCase();
}

function show(value?: string | number) {
	if (value === undefined || value === null || value === '') return '—';
	return String(value);
}

function creditsLabel(value?: number) {
	if (value === undefined || value === null) return '—';
	return formatPrice(Number(value));
}

// A NovaVida devolve várias chaves como palavras coladas em maiúsculas e sem
// separador (ex.: PESSOASLIGADAS, POSSIVELPROFISSAO), o que fica ilegível na
// tela. Este mapa traduz as chaves conhecidas para rótulos com espaços. A busca
// é feita pela chave em maiúsculas, com e sem underscores.
const LABEL_MAP: Record<string, string> = {
	// Seções
	SOCIEDADES: 'Sociedades',
	CONTATOSRUINS: 'Contatos ruins',
	PESSOASLIGADAS: 'Pessoas ligadas',
	PEPRELACIONADOS: 'PEP relacionados',
	ULTIMAEMPRESALIGADA: 'Última empresa ligada',
	EMPRESASLIGADAS: 'Empresas ligadas',
	ENDERECOS: 'Endereços',
	TELEFONES: 'Telefones',
	EMAILS: 'E-mails',
	// Campos cadastrais
	NOME: 'Nome',
	NOMEMAE: 'Nome da mãe',
	NOMEPAI: 'Nome do pai',
	CPF: 'CPF',
	RG: 'RG',
	NASC: 'Nascimento',
	NASCIMENTO: 'Nascimento',
	DATANASCIMENTO: 'Data de nascimento',
	IDADE: 'Idade',
	SEXO: 'Sexo',
	SIGNO: 'Signo',
	ESTADOCIVIL: 'Estado civil',
	NACIONALIDADE: 'Nacionalidade',
	NATURALIDADE: 'Naturalidade',
	ORGAOEMISSOR: 'Órgão emissor',
	ESTADORG: 'Estado do RG',
	DATAEXPEDICAO: 'Data de expedição',
	// Financeiro / score
	RENDA: 'Renda',
	RENDAESTIMADA: 'Renda estimada',
	FONTERENDA: 'Fonte de renda',
	FONTEDERENDA: 'Fonte de renda',
	SCORE: 'Score',
	SCORECREDITO: 'Score de crédito',
	SCOREDIGITAL: 'Score digital',
	MENSAGEMSCORE: 'Mensagem do score',
	PERSONACREDITO: 'Persona de crédito',
	CLASSEECONOMICA: 'Classe econômica',
	PATRIMONIO: 'Patrimônio',
	AUXILIOBRASIL: 'Auxílio Brasil',
	// Perfil / demográfico
	GERACAO: 'Geração',
	DIASEMANA: 'Dia da semana',
	POSSIVELPROFISSAO: 'Possível profissão',
	PERSONADEMOGRAFICA: 'Persona demográfica',
	POSSIVELESCOLARIDADE: 'Possível escolaridade',
	FLAGAGLOMERADOSUBNORMAL: 'Aglomerado subnormal',
	FLAGFGTS: 'FGTS',
	FLAGOBITO: 'Óbito',
	OBITO: 'Óbito',
	// Bancário
	BANCO: 'Banco',
	AGENCIA: 'Agência',
	CONTA: 'Conta',
	TIPOCONTA: 'Tipo de conta',
	CHAVEPIX: 'Chave PIX',
	// Vínculos / empresas
	VINCULO: 'Vínculo',
	RAZAOSOCIAL: 'Razão social',
	NOMEEMPRESA: 'Nome da empresa',
	CNPJ: 'CNPJ',
	PARTICIPACAO: 'Participação',
	CARGO: 'Cargo',
	DATAENTRADA: 'Data de entrada',
	// Contato
	DDD: 'DDD',
	TELEFONE: 'Telefone',
	OPERADORA: 'Operadora',
	FLWHATS: 'WhatsApp',
	EMAIL: 'E-mail',
	POSICAO: 'Posição',
	LOGRADOURO: 'Logradouro',
	NUMERO: 'Número',
	COMPLEMENTO: 'Complemento',
	BAIRRO: 'Bairro',
	CIDADE: 'Cidade',
	UF: 'UF',
	CEP: 'CEP'
};

// Rótulo legível a partir da chave crua da NovaVida. Usa o mapa de traduções e,
// para chaves desconhecidas, apenas troca underscores por espaços.
function prettifyKey(key: string) {
	const raw = (key || '').trim().toUpperCase();
	const noUnderscore = raw.replace(/_/g, '');
	return LABEL_MAP[raw] || LABEL_MAP[noUnderscore] || key.replace(/_/g, ' ').trim();
}

function formatValue(value: any) {
	if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
	return String(value);
}

// Chaves que representam data de nascimento (NovaVida/Wesend variam o nome).
const DATE_KEYS = new Set(['NASC', 'NASCIMENTO', 'DATA_NASCIMENTO', 'DTNASC', 'DATANASC', 'DT_NASCIMENTO', 'DATA_NASC']);

// Normaliza data de nascimento para dd/MM/yyyy a partir dos formatos que a
// NovaVida costuma devolver (yyyymmdd, yyyy-mm-dd, ddmmyyyy) — ou devolve como veio.
function formatBirthDate(value: any): string {
	if (value === null || value === undefined) return '';
	const s = String(value).trim();
	if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) return s;
	let m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (m) return `${m[3]}/${m[2]}/${m[1]}`;
	m = s.match(/^(\d{4})(\d{2})(\d{2})$/);
	if (m) return `${m[3]}/${m[2]}/${m[1]}`;
	m = s.match(/^(\d{2})(\d{2})(\d{4})$/);
	if (m) return `${m[1]}/${m[2]}/${m[3]}`;
	return s;
}

// Valor exibido para uma chave: formata a data de nascimento; demais campos usam
// o formatador padrão.
function displayValue(key: string, value: any) {
	if (DATE_KEYS.has(key.toUpperCase())) return formatBirthDate(value);
	return formatValue(value);
}

function isFilled(value: any) {
	return value !== null && value !== undefined && String(value).trim() !== '' && typeof value !== 'object';
}

// Seções já renderizadas de forma dedicada — as demais chaves são exibidas
// genericamente para garantir "todos os dados disponíveis".
const KNOWN_SECTIONS = new Set(['CADASTRAIS', 'ENDERECOS', 'TELEFONES', 'EMAILS']);

export default function ResultCard({ result }: ResultCardProps) {
	const textColor = useColorModeValue('navy.700', 'white');
	const subColor = useColorModeValue('secondaryGray.700', 'secondaryGray.500');
	const tileBg = useColorModeValue('secondaryGray.300', 'navy.900');

	const data = result.data;
	const cad = data?.CADASTRAIS;

	if (!data || !cad) {
		return null;
	}

	const copyData = () => {
		navigator.clipboard.writeText(JSON.stringify(data, null, 2));
		toastService.success('Dados copiados para a área de transferência');
	};

	// Clicar em cima de um dado copia só aquele valor.
	const copyField = (text?: string | number) => {
		const value = text === undefined || text === null ? '' : String(text).trim();
		if (!value || value === '—') return;
		navigator.clipboard.writeText(value);
		toastService.success(`Copiado: ${value}`);
	};

	// Props reaproveitadas nos "tiles" de dados para o clique-para-copiar.
	const clickToCopy = (value?: string | number) => ({
		cursor: 'pointer' as const,
		title: 'Clique para copiar',
		transition: '.15s all ease',
		_hover: { boxShadow: 'sm', opacity: 0.9 },
		onClick: () => copyField(value)
	});

	const copyPhone = (ddd?: string | number, phone?: string | number) => {
		const digits = `${ddd ?? ''}${phone ?? ''}`.replace(/\D/g, '');
		if (!digits) return;
		navigator.clipboard.writeText(digits);
		toastService.success(`Telefone ${digits} copiado`);
	};

	// Todos os campos cadastrais preenchidos (inclui os que não têm tile próprio).
	const cadEntries = Object.entries(cad).filter(([, v]) => isFilled(v));

	// Qualquer outra seção retornada pelo provedor (arrays de objetos) além das
	// já exibidas — renderizada genericamente para não esconder nenhum dado.
	const extraSections = Object.entries(data as Record<string, any>).filter(
		([k, v]) => !KNOWN_SECTIONS.has(k) && Array.isArray(v) && v.length > 0 && typeof v[0] === 'object'
	) as [string, Record<string, any>[]][];

	// Campos escalares extras no topo do payload (fora de CADASTRAIS).
	const extraScalars = Object.entries(data as Record<string, any>).filter(
		([k, v]) => !KNOWN_SECTIONS.has(k) && isFilled(v)
	);

	return (
		<Card mt='20px'>
			{/* Header */}
			<Flex justify='space-between' align={{ base: 'flex-start', md: 'center' }} direction={{ base: 'column', md: 'row' }} gap='12px'>
				<Flex align='center' gap='16px'>
					<Avatar name={initialsOf(cad.NOME)} bg='brand.500' color='white' size='lg' />
					<Box>
						<Text fontSize='22px' fontWeight='800' color={textColor} cursor='pointer' title='Clique para copiar' onClick={() => copyField(cad.NOME)}>
							{cad.NOME}
						</Text>
						<Text color={subColor} fontSize='sm' cursor='pointer' title='Clique para copiar CPF' onClick={() => copyField(cad.CPF)}>
							{cad.CPF}
							{cad.IDADE ? ` · ${cad.IDADE} anos` : ''}
							{cad.SEXO ? ` · ${cad.SEXO}` : ''}
						</Text>
					</Box>
				</Flex>
				<HStack>
					<Badge borderRadius='10px' px='12px' py='6px' bg='brand.50' color='brand.600'>
						Custo: {creditsLabel(result.cost)}
					</Badge>
				</HStack>
			</Flex>

			<Divider my='20px' />

			{/* Metric tiles */}
			<SimpleGrid columns={{ base: 1, md: 3 }} spacing='16px'>
				<Box bg={tileBg} borderRadius='16px' p='18px' {...clickToCopy(show(cad.RENDA))}>
					<Flex align='center' gap='8px' color='brand.500'>
						<Icon as={MdTrendingUp} />
						<Text fontSize='xs' fontWeight='700' color={subColor}>
							RENDA ESTIMADA
						</Text>
					</Flex>
					<Text fontSize='22px' fontWeight='800' color={textColor} mt='6px'>
						{show(cad.RENDA)}
					</Text>
				</Box>
				<Box bg={tileBg} borderRadius='16px' p='18px' {...clickToCopy(show(cad.SCORE ?? cad.SCORE_CREDITO))}>
					<Flex align='center' gap='8px' color='cyan.500'>
						<Icon as={MdCreditScore} />
						<Text fontSize='xs' fontWeight='700' color={subColor}>
							SCORE DE CRÉDITO
						</Text>
					</Flex>
					<Text fontSize='22px' fontWeight='800' color={textColor} mt='6px'>
						{show(cad.SCORE ?? cad.SCORE_CREDITO)}
					</Text>
				</Box>
				<Box bg={tileBg} borderRadius='16px' p='18px'>
					<Text fontSize='xs' fontWeight='700' color={subColor}>
						FGTS
					</Text>
					<Text
						fontSize='22px'
						fontWeight='800'
						mt='6px'
						color={isFlag(cad.FLAG_FGTS) ? 'green.500' : 'secondaryGray.600'}>
						{isFlag(cad.FLAG_FGTS) ? 'Elegível' : 'Não elegível'}
					</Text>
				</Box>
			</SimpleGrid>

			{/* Two columns: Telefones + Endereço */}
			<Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap='24px' mt='24px'>
				<Box>
					<Text fontWeight='700' color={textColor} mb='12px'>
						Telefones
					</Text>
					{(data.TELEFONES || []).length === 0 && (
						<Text color={subColor} fontSize='sm'>
							Nenhum telefone encontrado.
						</Text>
					)}
					{(data.TELEFONES || []).map((tel, i) => {
						const whats = isFlag(tel.FLWHATS);
						const hasNumber = !!`${tel.TELEFONE ?? ''}`.replace(/\D/g, '');
						return (
							<Flex key={i} align='center' gap='10px' mb='10px'>
								<Icon as={whats ? FaWhatsapp : MdPhone} color={whats ? 'green.500' : 'secondaryGray.600'} />
								<Text color={textColor} fontSize='sm'>
									({tel.DDD}) {tel.TELEFONE}
								</Text>
								{hasNumber && (
									<Tooltip label='Copiar telefone' fontSize='xs'>
										<IconButton
											aria-label={`Copiar telefone ${tel.DDD}${tel.TELEFONE}`}
											icon={<Icon as={MdContentCopy} />}
											size='xs'
											variant='ghost'
											color='secondaryGray.600'
											onClick={() => copyPhone(tel.DDD, tel.TELEFONE)}
										/>
									</Tooltip>
								)}
								{whats && (
									<Badge colorScheme='green' borderRadius='8px' fontSize='10px'>
										WhatsApp
									</Badge>
								)}
							</Flex>
						);
					})}
				</Box>

				<Box>
					<Text fontWeight='700' color={textColor} mb='12px'>
						Endereço
					</Text>
					{(data.ENDERECOS || []).length === 0 && (
						<Text color={subColor} fontSize='sm'>
							Nenhum endereço encontrado.
						</Text>
					)}
					{(data.ENDERECOS || []).map((end, i) => (
						<Flex key={i} align='flex-start' gap='10px' mb='10px'>
							<Icon as={MdLocationOn} color='brand.500' mt='2px' />
							<Text color={textColor} fontSize='sm'>
								{[end.LOGRADOURO, end.NUMERO, end.COMPLEMENTO].filter(Boolean).join(', ')}
								{end.BAIRRO ? ` - ${end.BAIRRO}` : ''}
								<br />
								{[end.CIDADE, end.UF].filter(Boolean).join('/')} {end.CEP ? `· ${end.CEP}` : ''}
							</Text>
						</Flex>
					))}
				</Box>
			</Grid>

			{(data.EMAILS || []).filter(e => e.EMAIL).length > 0 && (
				<Box mt='24px'>
					<Text fontWeight='700' color={textColor} mb='12px'>
						E-mails
					</Text>
					<Flex wrap='wrap' gap='10px'>
						{(data.EMAILS || [])
							.filter(e => e.EMAIL)
							.map((em, i) => (
								<Flex key={i} align='center' gap='8px' bg={tileBg} borderRadius='10px' px='12px' py='8px'>
									<Icon as={MdEmail} color='brand.500' />
									<Text color={textColor} fontSize='sm'>
										{em.EMAIL}
									</Text>
								</Flex>
							))}
					</Flex>
				</Box>
			)}

			<Divider my='20px' />

			{/* Dados completos: todos os campos disponíveis do provedor */}
			<Box>
				<Text fontWeight='700' color={textColor} mb='12px'>
					Dados completos
				</Text>
				<SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing='12px'>
					{cadEntries.map(([k, v]) => (
						<Box key={k} bg={tileBg} borderRadius='12px' px='14px' py='10px' {...clickToCopy(displayValue(k, v))}>
							<Text fontSize='10px' fontWeight='700' color={subColor} textTransform='uppercase' letterSpacing='0.04em'>
								{prettifyKey(k)}
							</Text>
							<Text fontSize='sm' color={textColor} fontWeight='600' wordBreak='break-word'>
								{displayValue(k, v)}
							</Text>
						</Box>
					))}
					{extraScalars.map(([k, v]) => (
						<Box key={k} bg={tileBg} borderRadius='12px' px='14px' py='10px' {...clickToCopy(displayValue(k, v))}>
							<Text fontSize='10px' fontWeight='700' color={subColor} textTransform='uppercase' letterSpacing='0.04em'>
								{prettifyKey(k)}
							</Text>
							<Text fontSize='sm' color={textColor} fontWeight='600' wordBreak='break-word'>
								{displayValue(k, v)}
							</Text>
						</Box>
					))}
				</SimpleGrid>
			</Box>

			{extraSections.map(([sectionKey, rows]) => (
				<Box mt='20px' key={sectionKey}>
					<Text fontWeight='700' color={textColor} mb='12px'>
						{prettifyKey(sectionKey)}
					</Text>
					{rows.map((row, i) => (
						<Flex key={i} wrap='wrap' gap='8px' mb='8px'>
							{Object.entries(row)
								.filter(([, v]) => isFilled(v))
								.map(([k, v]) => (
									<Box key={k} bg={tileBg} borderRadius='10px' px='10px' py='6px' {...clickToCopy(displayValue(k, v))}>
										<Text as='span' fontSize='10px' color={subColor} textTransform='uppercase'>
											{prettifyKey(k)}:{' '}
										</Text>
										<Text as='span' fontSize='sm' color={textColor} fontWeight='600'>
											{displayValue(k, v)}
										</Text>
									</Box>
								))}
						</Flex>
					))}
				</Box>
			))}

			<Divider my='20px' />

			{/* Footer buttons */}
			<Flex gap='12px' justify='flex-end' direction={{ base: 'column', sm: 'row' }}>
				<Button variant='lightBrand' leftIcon={<Icon as={MdContentCopy} />} onClick={copyData}>
					Copiar dados
				</Button>
			</Flex>
		</Card>
	);
}
